import React, { useState, useEffect, useCallback } from 'react';
import { LogIn, LogOut, Coffee, Play, Timer, ChevronDown } from 'lucide-react';
import { attendanceAPI } from '../utils/api';
import { toast } from 'sonner';

function formatMinutes(mins) {
  if (!mins && mins !== 0) return '0h 0m';
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${h}h ${m}m`;
}

const STATUS_CONFIG = {
  not_started: { dot: 'bg-slate-400', label: 'Clock In', labelColor: 'text-slate-500' },
  active:      { dot: 'bg-emerald-500 animate-pulse', label: 'Active', labelColor: 'text-emerald-700' },
  on_break:    { dot: 'bg-amber-400 animate-pulse', label: 'On Break', labelColor: 'text-amber-700' },
  punched_out: { dot: 'bg-slate-400', label: 'Clocked Out', labelColor: 'text-slate-500' },
};

export default function AttendanceHeaderButton() {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  const [open, setOpen] = useState(false);
  const [elapsed, setElapsed] = useState(0);

  const fetchSession = useCallback(async () => {
    try {
      const { data } = await attendanceAPI.getToday();
      setSession(data);
      setElapsed(data.log?.elapsed_minutes || 0);
    } catch {
      setSession(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSession(); }, [fetchSession]);

  useEffect(() => {
    if (session?.status !== 'active' && session?.status !== 'on_break') return;
    const t = setInterval(() => setElapsed(p => p + 1), 60000);
    return () => clearInterval(t);
  }, [session?.status]);

  const act = async (fn, successMsg) => {
    setActing(true);
    try {
      await fn();
      toast.success(successMsg);
      await fetchSession();
      setOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Action failed');
    } finally {
      setActing(false);
    }
  };

  if (loading) return null;

  const status = session?.status || 'not_started';
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.not_started;
  const log = session?.log;

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 transition-colors text-sm"
      >
        <span className={`w-2 h-2 rounded-full flex-shrink-0 ${cfg.dot}`} />
        <span className={`font-medium hidden sm:block ${cfg.labelColor}`}>{cfg.label}</span>
        {(status === 'active' || status === 'on_break') && (
          <span className="font-mono text-xs text-slate-500 hidden md:block">
            <Timer className="inline w-3 h-3 mr-0.5" />{formatMinutes(elapsed)}
          </span>
        )}
        <ChevronDown className={`w-3.5 h-3.5 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-2 w-56 bg-white border border-slate-200 rounded-xl shadow-xl z-50 overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100 bg-slate-50">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Attendance</p>
              {log?.punch_in_time && (
                <p className="text-xs text-slate-400 mt-0.5">
                  In: {new Date(log.punch_in_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  {log.punch_out_time && ` · Out: ${new Date(log.punch_out_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`}
                </p>
              )}
              {(status === 'active' || status === 'on_break') && (
                <p className="text-xs font-mono font-semibold text-slate-700 mt-0.5">
                  Total: {formatMinutes(elapsed)}
                </p>
              )}
              {status === 'punched_out' && log?.total_work_minutes > 0 && (
                <p className="text-xs font-mono font-semibold text-slate-700 mt-0.5">
                  Total: {formatMinutes(log.total_work_minutes)}
                </p>
              )}
            </div>

            <div className="p-2 space-y-1">
              {status === 'not_started' && (
                <button
                  onClick={() => act(attendanceAPI.punchIn, 'Clocked in! Have a productive day.')}
                  disabled={acting}
                  className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium transition-colors disabled:opacity-60"
                >
                  <LogIn className="w-4 h-4" /> Clock In
                </button>
              )}
              {status === 'active' && (
                <>
                  <button
                    onClick={() => act(attendanceAPI.breakStart, 'Break started. Enjoy!')}
                    disabled={acting}
                    className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg border border-amber-300 text-amber-700 hover:bg-amber-50 text-sm font-medium transition-colors disabled:opacity-60"
                  >
                    <Coffee className="w-4 h-4" /> Take a Break
                  </button>
                  <button
                    onClick={() => act(attendanceAPI.punchOut, 'Clocked out. Great work today!')}
                    disabled={acting}
                    className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-sm font-medium transition-colors disabled:opacity-60"
                  >
                    <LogOut className="w-4 h-4" /> Clock Out
                  </button>
                </>
              )}
              {status === 'on_break' && (
                <>
                  <button
                    onClick={() => act(attendanceAPI.breakEnd, 'Welcome back!')}
                    disabled={acting}
                    className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium transition-colors disabled:opacity-60"
                  >
                    <Play className="w-4 h-4" /> Resume Work
                  </button>
                  <button
                    onClick={() => act(attendanceAPI.punchOut, 'Clocked out. Great work today!')}
                    disabled={acting}
                    className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-sm font-medium transition-colors disabled:opacity-60"
                  >
                    <LogOut className="w-4 h-4" /> Clock Out
                  </button>
                </>
              )}
              {status === 'punched_out' && (
                <button
                  onClick={() => act(attendanceAPI.punchIn, 'Clocked in! Have a productive day.')}
                  disabled={acting}
                  className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium transition-colors disabled:opacity-60"
                >
                  <LogIn className="w-4 h-4" /> Clock In Again
                </button>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
